import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, CircularProgress, List, ListItem, Divider, Tooltip, IconButton } from '@mui/material';
import { Fullscreen, FullscreenExit } from '@mui/icons-material';
import { useDashboardData } from '../../../contexts/DashboardDataContext';
import { Card, CardContent } from '@mui/material';
import { useDashboardContext } from '../../Dashboard';

const SamsungServiceStatusWidget = () => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { samsungServiceData, fetchServiceAnalysis } = useDashboardData();
    const { currentZone } = useDashboardContext();
    const { loading, data, error } = samsungServiceData;
    const [dataInitialized, setDataInitialized] = useState(false);

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text)
            .catch(err => console.error('Failed to copy text: ', err));
    };

    useEffect(() => {
        // If we already have data, mark as initialized
        if (data) {
            console.log('[SamsungServiceStatusWidget] Using existing data from context');
            setDataInitialized(true);
            return;
        }
        
        // Only fetch if we don't have data and aren't currently loading
        if (!loading && !error && !dataInitialized) {
            console.log('[SamsungServiceStatusWidget] No data and not loading, triggering fetch');
            fetchServiceAnalysis();
        }
    }, [data, loading, error, fetchServiceAnalysis, dataInitialized]);

    const getDetailedTooltipContent = (order) => (
        <Box sx={{ p: 1, maxWidth: 300 }}>
            {/* Service Info Section */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, color: '#388bfd', fontWeight: 'bold' }}>
                    Service Information
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Order:</strong> {order.svcOrderNo}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>ASC Job:</strong> {order.ascJobNo}
                </Typography>
                <Typography variant="body2">
                    <strong>Created:</strong> {order.postingDate ? 
                        new Date(order.postingDate.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')).toLocaleDateString() 
                        : 'N/A'}
                </Typography>
            </Box>

            {/* Product Info Section */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, color: '#388bfd', fontWeight: 'bold' }}>
                    Product Details
                </Typography>
                <Typography variant="body2">
                    <strong>Model:</strong> {order.modelCode}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Serial:</strong> {order.serialNo || 'N/A'}
                </Typography>
                {order.imei && (
                    <Typography variant="body2">
                        <strong>IMEI:</strong> {order.imei}
                    </Typography>
                )}
            </Box>

            {/* Status Section */}
            <Box>
                <Typography variant="body2" sx={{ mb: 1, color: '#388bfd', fontWeight: 'bold' }}>
                    Current Status
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Status:</strong> {order.statusDescription} 
                    <Typography component="span" variant="caption" color="text.secondary">
                        {' '}({order.status})
                    </Typography>
                </Typography>
                <Typography variant="body2">
                    <strong>Reason:</strong> {order.reasonDescription}
                    <Typography component="span" variant="caption" color="text.secondary">
                        {' '}({order.reason})
                    </Typography>
                </Typography>
            </Box>
        </Box>
    );

    const toggleSize = () => {
        setIsExpanded(!isExpanded);
    };

    // Get the oldest service orders (highest days_in_service)
    const oldestServiceOrders = data?.in_warranty_details
        ?.filter(detail => detail.days_in_service > 0)
        ?.sort((a, b) => b.days_in_service - a.days_in_service)
        ?.slice(0, isExpanded ? 10 : 3) || [];  // Changed from 5 to 3 for minimized view

    // Show loading state only when we have no data
    if (loading && !data) {
        return (
            <Card sx={{ width: "600px", height: "330px", backgroundColor: "#1e1e1e", color: "#fff" }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Samsung Service Status</Typography>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "250px" }}>
                        <CircularProgress color="inherit" size={40} />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    // Show error state only when we have no data
    if (error && !data) {
        return (
            <Card sx={{ width: "600px", height: "330px", backgroundColor: "#1e1e1e", color: "#fff" }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Samsung Service Status</Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "250px" }}>
                        <Typography color="error" gutterBottom>Failed to load Samsung service data</Typography>
                        <button onClick={fetchServiceAnalysis} style={{ marginTop: 10 }}>Refresh Data</button>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    // No data state
    if (!data || data.length === 0) {
        return (
            <Card sx={{ width: "600px", height: "330px", backgroundColor: "#1e1e1e", color: "#fff" }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Samsung Service Status</Typography>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "250px" }}>
                        <Typography>No Samsung LP orders found</Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Box
            sx={{
                p: 2,
                bgcolor: '#1e1e1e',
                borderRadius: 2,
                color: 'white',
                width: 350,
                height: 'auto',
                transition: 'all 0.3s ease-in-out',
                ...(isExpanded && {
                    maxHeight: '80vh',
                    overflow: 'auto'
                })
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <h3>LP Pending Days</h3>  {/* Endret til h3 for å matche Category widget */}
                <IconButton 
                    onClick={toggleSize}
                    size="small"
                    sx={{ 
                        color: '#388bfd',
                        '&:hover': {
                            color: '#58a6fd'
                        }
                    }}
                >
                    {isExpanded ? <FullscreenExit /> : <Fullscreen />}
                </IconButton>
            </Box>

            <Paper sx={{ 
                p: 2, 
                bgcolor: '#2d2d2d',
                height: isExpanded ? 'calc(80vh - 100px)' : '230px',  // Redusert fra 300px
                transition: 'height 0.3s ease-in-out',
                overflow: 'auto',
                // Custom scrollbar styling
                '&::-webkit-scrollbar': {
                    width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                    background: '#252526',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(255, 255, 255, 0.15)',
                    borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    background: 'rgba(255, 255, 255, 0.25)',
                },
                // For Firefox
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(255, 255, 255, 0.15) #252526',
            }}>
                <List sx={{ width: '100%' }}>
                    {oldestServiceOrders.length > 0 ? (
                        oldestServiceOrders.map((order, index) => (
                            <React.Fragment key={index}>
                                <Tooltip 
                                    title={getDetailedTooltipContent(order)}
                                    arrow
                                    placement="right"
                                    sx={{
                                        bgcolor: '#424242',
                                        '& .MuiTooltip-arrow': {
                                            color: '#424242'
                                        }
                                    }}
                                >
                                    <ListItem sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        py: 1,
                                        bgcolor: order.days_in_service > 7 ? 'rgba(211, 47, 47, 0.1)' : 'transparent',
                                        '&:hover': {
                                            bgcolor: 'rgba(255, 255, 255, 0.05)'
                                        }
                                    }}>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            flexDirection: 'column',
                                            width: isExpanded ? '80%' : 'auto'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography 
                                                    variant="body2" 
                                                    fontWeight="bold"
                                                    onClick={() => copyToClipboard(order.svcOrderNo)}
                                                    sx={{ 
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            textDecoration: 'underline'
                                                        }
                                                    }}
                                                >
                                                    {order.svcOrderNo}
                                                </Typography>
                                                <Typography 
                                                    variant="caption" 
                                                    color="text.secondary"
                                                    onClick={() => copyToClipboard(order.ascJobNo)}
                                                    sx={{ 
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            textDecoration: 'underline'
                                                        }
                                                    }}
                                                >
                                                    #{order.ascJobNo}
                                                </Typography>
                                            </Box>
                                            <Typography variant="caption" color="text.secondary">
                                                {order.modelCode ? order.modelCode.substring(0, 8) : 'N/A'}
                                            </Typography>
                                            {isExpanded && (
                                                <>
                                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                                        <strong>Created:</strong> {order.postingDate ? 
                                                            new Date(order.postingDate.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')).toLocaleDateString() 
                                                            : 'N/A'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        <strong>Status:</strong> {order.statusDescription}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ 
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        width: '95%'
                                                    }}>
                                                        <strong>Reason:</strong> {order.reasonDescription}
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            bgcolor: order.days_in_service > 7 ? 'error.dark' : 'primary.dark',
                                            borderRadius: '50%',
                                            width: 40,
                                            height: 40,
                                            color: 'white',
                                            flexShrink: 0
                                        }}>
                                            <Typography>
                                                {order.days_in_service}
                                            </Typography>
                                        </Box>
                                    </ListItem>
                                </Tooltip>
                                {index < oldestServiceOrders.length - 1 && <Divider variant="middle" sx={{ bgcolor: '#444' }} />}
                            </React.Fragment>
                        ))
                    ) : (
                        <ListItem>
                            <Typography variant="body2" sx={{ width: '100%', textAlign: 'center' }}>No in-warranty cases found</Typography>
                        </ListItem>
                    )}
                </List>
            </Paper>
        </Box>
    );
};

export default SamsungServiceStatusWidget;
